import {
  AppBar,
  Dialog,
  IconButton,
  Slide,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  CategoryKey,
  SeatingChart,
  SeatsioSeatingChart,
} from '@seatsio/seatsio-react';
import {
  addSeatsToCart,
  addSeatSubtypesToCart,
  removeAllTheSelectedItems,
} from 'app/pages/TicketSalePage/slice';
import {
  makeSelectedEvent,
  selectTicketSaleIsLoading,
  selectVoucherData,
} from 'app/pages/TicketSalePage/slice/selectors';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { clearCartData, startSeatsCountdown } from 'store/cartSlice';
import { selectCartData } from 'store/cartSlice/selectors';
import { setReportsByAvailability } from 'store/seatingChart';
import { formatMoney } from 'utils/formatMoney';
import { formatTicketsPricingForSeatingChart } from 'utils/formatTicketsPricingForSeatingChart';
import { OveitIcon } from '../OveitIcon';
import { formatReportsByAvailability } from './formatReportsByAvailability';
import SeatingChartSkeleton from './SeatingChartSkeleton';
import { ArrowRight, X } from 'lucide-react';
import { Button } from '../ui/button';

interface SeatingChartProps {
  setCountdownIsOnGoing: (status: boolean) => void;
  chartIsOpen: boolean;
  handleCloseChart: () => void;
  availableCategories: CategoryKey[];
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SeatingChartWrapper: React.FC<SeatingChartProps> = ({
  setCountdownIsOnGoing,
  chartIsOpen,
  handleCloseChart,
  availableCategories,
}) => {
  const { t } = useTranslation();
  const isLoading = useSelector(selectTicketSaleIsLoading);
  const { holdToken } = useSelector(selectCartData);
  const dispatch = useDispatch();
  const selectedEvent = useSelector(makeSelectedEvent);
  const {
    voucherApplicableTickets,
    voucherData: { discount, discount_percent },
  } = useSelector(selectVoucherData);
  const updateQuantitiesIntervalRef = useRef<NodeJS.Timer | null>(null);

  const isLargeScreen = useMediaQuery('(min-width: 768px)');

  const cleanupInterval = () => {
    if (updateQuantitiesIntervalRef.current) {
      clearInterval(updateQuantitiesIntervalRef.current);
      updateQuantitiesIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => cleanupInterval();
  }, []);

  if (isLoading && isLargeScreen) return <SeatingChartSkeleton />;

  const handleObjectSelection = object => {
    const ticketType = selectedEvent.tickets.find(
      ticket => ticket.seatsCategoryKey == object.category?.key,
    );

    console.log('addSeatSubtypesToCart:', object);

    if (ticketType?.subtypes) {
      if (object.objectType === 'GeneralAdmissionArea') {
        for (let ticketTypeId in object.selectionPerTicketType) {
          dispatch(
            addSeatSubtypesToCart({
              ticketId: ticketType.id,
              quantity:
                object.objectType === 'GeneralAdmissionArea'
                  ? object.selectionPerTicketType[ticketTypeId]
                  : object.status === 'free'
                  ? 0
                  : 1,
              seatId: object.seatId,
              seatLabel: object.label,
              subtypeId: ticketTypeId,
            }),
          );
        }
      } else if (object.objectType === 'Seat') {
        dispatch(
          addSeatSubtypesToCart({
            ticketId: ticketType!.id,
            quantity: object.status === 'free' ? 0 : 1,
            seatId: object.seatId,
            seatLabel: object.label,
            subtypeId: object.selectedTicketType,
          }),
        );
      }
    } else {
      dispatch(
        addSeatsToCart({
          ticketId: ticketType!.id,
          quantity:
            object.objectType === 'GeneralAdmissionArea'
              ? object.numSelected
              : object.status === 'free'
              ? 0
              : 1,
          seatId: object.seatId,
          seatLabel: object.label,
        }),
      );
    }
  };

  const updateSeatsioAvailableQuantities = async (chart: SeatingChart) => {
    const reports = await chart.getReportBySelectability();

    dispatch(setReportsByAvailability(formatReportsByAvailability(reports)));
  };

  const handleHoldTokenExpired = () => {
    setCountdownIsOnGoing(false);
    dispatch(clearCartData());
    dispatch(removeAllTheSelectedItems());
  };

  const handleChartRendered = async (chart: SeatingChart) => {
    await updateSeatsioAvailableQuantities(chart);

    cleanupInterval();

    updateQuantitiesIntervalRef.current = setInterval(() => {
      updateSeatsioAvailableQuantities(chart);
    }, 3000);
  };

  const seatingChart = (
    <SeatsioSeatingChart
      region="na"
      workspaceKey={selectedEvent.seatingChart?.workspaceKey || ''}
      event={selectedEvent.seatingChart?.event}
      legend={{
        hideNonSelectableCategories: true,
      }}
      maxSelectedObjects={20}
      showFullScreenButton={isLargeScreen}
      pricing={formatTicketsPricingForSeatingChart(
        selectedEvent.tickets,
        selectedEvent.tax_included,
        selectedEvent.fee_included,
        selectedEvent.fees,
        selectedEvent.tax_rate,
        voucherApplicableTickets,
        discount,
        discount_percent,
      )}
      priceFormatter={price => formatMoney(price, selectedEvent.currency)}
      availableCategories={availableCategories}
      messages={selectedEvent.tickets.reduce((acc, ticket) => {
        acc[ticket.seatsCategoryName] = ticket.name;
        return acc;
      }, {})}
      session={holdToken?.token ? 'manual' : 'continue'}
      holdToken={holdToken?.token}
      onHoldTokenExpired={handleHoldTokenExpired}
      onSessionInitialized={tokenData => {
        setCountdownIsOnGoing(true);
        dispatch(startSeatsCountdown(tokenData));
      }}
      onObjectSelected={handleObjectSelection}
      onObjectDeselected={handleObjectSelection}
      onChartRendered={handleChartRendered}
    />
  );

  if (isLargeScreen) return seatingChart;

  return (
    <Dialog
      keepMounted
      fullScreen
      open={chartIsOpen}
      onClose={handleCloseChart}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ backgroundColor: 'var(--primary)' }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <div className="w-[100px]">
            <IconButton edge="start" color="inherit" onClick={handleCloseChart}>
              <X />
            </IconButton>
          </div>
          <Typography
            component="h2"
            textAlign={'center'}
            sx={{ color: 'var(--neutral-0)' }}
            className="title-md"
          >
            {t('ticketSale.chooseSeats')}
          </Typography>

          <Button onClick={handleCloseChart}>
            {t('common.continue')}
            <ArrowRight />
          </Button>
        </Toolbar>
      </AppBar>
      {seatingChart}
    </Dialog>
  );
};

export default SeatingChartWrapper;
